import { supabase } from '@/integrations/supabase/client';
import { Tenant, DEFAULT_TENANT_BRANDING, DEFAULT_TENANT_SETTINGS } from '../types/tenant';
import { createDefaultTenant, isValidTenantSlug } from '../utils/tenant-utils';

export class TenantService {
  /**
   * Get tenant by slug
   */
  static async getTenantBySlug(slug: string): Promise<Tenant | null> {
    try {
      if (!isValidTenantSlug(slug)) {
        throw new Error(`Invalid tenant slug: ${slug}`);
      }

      const { data, error } = await (supabase as any)
        .from('watt_workspaces')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }

      return this.transformDatabaseTenant(data);
    } catch (error) {
      console.error('Error fetching tenant:', error);
      return null;
    }
  }

  /**
   * Get tenant by ID
   */
  static async getTenantById(id: string): Promise<Tenant | null> {
    try {
      const { data, error } = await (supabase as any)
        .from('watt_workspaces')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return this.transformDatabaseTenant(data);
    } catch (error) {
      console.error('Error fetching tenant by ID:', error);
      return null;
    }
  }

  /**
   * Create a new tenant and add current user as admin
   */
  static async createTenant(tenantData: Partial<Tenant>): Promise<Tenant | null> {
    try {
      if (!tenantData.slug || !isValidTenantSlug(tenantData.slug)) {
        throw new Error('Valid tenant slug is required');
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to create workspace');
      }

      // Check if slug exists and find a unique one
      let uniqueSlug = tenantData.slug;
      let counter = 1;
      
      while (await this.tenantExists(uniqueSlug)) {
        uniqueSlug = `${tenantData.slug}-${counter}`;
        counter++;
        // Prevent infinite loop
        if (counter > 100) {
          throw new Error('Unable to generate unique workspace slug');
        }
      }

      const { data, error } = await (supabase as any)
        .from('watt_workspaces')
        .insert({
          slug: uniqueSlug,
          name: tenantData.name || uniqueSlug,
          domain: tenantData.domain || null
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Create workspace membership for the current user as admin
      const { error: membershipError } = await (supabase as any)
        .from('workspace_memberships')
        .insert([{
          workspace_id: data.id,
          user_id: user.id,
          role: 'workspace_admin',
          status: 'active'
        }]);

      if (membershipError) {
        console.error('Error creating workspace membership:', membershipError);
        // Don't fail workspace creation if membership fails
        // The user can be added manually later
      }

      return this.transformDatabaseTenant(data);
    } catch (error) {
      console.error('Error creating tenant:', error);
      return null;
    }
  }

  /**
   * Update tenant
   */
  static async updateTenant(id: string, updates: Partial<Tenant>): Promise<Tenant | null> {
    try {
      const { data, error } = await (supabase as any)
        .from('watt_workspaces')
        .update({
          name: updates.name,
          domain: updates.domain,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return this.transformDatabaseTenant(data);
    } catch (error) {
      console.error('Error updating tenant:', error);
      return null;
    }
  }

  /**
   * Delete tenant (admin only)
   */
  static async deleteTenant(id: string): Promise<boolean> {
    try {
      const { error } = await (supabase as any)
        .from('watt_workspaces')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error deleting tenant:', error);
      return false;
    }
  }

  /**
   * List all tenants (admin only)
   */
  static async listTenants(): Promise<Tenant[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('watt_workspaces')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data.map(this.transformDatabaseTenant);
    } catch (error) {
      console.error('Error listing tenants:', error);
      return [];
    }
  }

  /**
   * Check if tenant exists
   */
  static async tenantExists(slug: string): Promise<boolean> {
    try {
      const { data, error } = await (supabase as any)
        .from('watt_workspaces')
        .select('id')
        .eq('slug', slug)
        .single();

      return !error && !!data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get or create default tenant
   */
  static async getOrCreateDefaultTenant(): Promise<Tenant> {
    try {
      // Try to get existing default tenant
      let tenant = await this.getTenantBySlug('default');
      
      if (!tenant) {
        // Create default tenant if it doesn't exist
        tenant = await this.createTenant({
          slug: 'default',
          name: 'Default Workspace'
        });
      }

      return tenant || createDefaultTenant();
    } catch (error) {
      console.error('Error getting/creating default tenant:', error);
      return createDefaultTenant();
    }
  }

  /**
   * Set current tenant context in Supabase
   */
  static async setTenantContext(tenantId: string): Promise<void> {
    try {
      // This will be used by RLS policies
      await (supabase as any).rpc('set_tenant_context', { tenant_id: tenantId });
    } catch (error) {
      console.error('Error setting tenant context:', error);
    }
  }

  /**
   * Auto-assign tenant based on email domain
   */
  static async autoAssignTenantByEmail(email: string): Promise<string> {
    try {
      // Call the database function
      const { data, error } = await (supabase as any).rpc('auto_assign_tenant_by_email', { 
        user_email: email 
      });

      if (error) {
        console.error('Error auto-assigning tenant:', error);
        // Fall back to default tenant
        const defaultTenant = await this.getTenantBySlug('default');
        return defaultTenant?.id || 'default';
      }

      return data || 'default';
    } catch (error) {
      console.error('Error in autoAssignTenantByEmail:', error);
      // Fall back to default tenant
      const defaultTenant = await this.getTenantBySlug('default');
      return defaultTenant?.id || 'default';
    }
  }

  /**
   * Transform database tenant to application tenant
   * Provides defaults for V2 columns that may not exist in minimal V3 structure
   */
  private static transformDatabaseTenant(data: any): Tenant {
    return {
      id: data.id,
      slug: data.slug,
      name: data.name,
      domain: data.domain || null,
      // Provide defaults for V2 columns that may not exist in V3
      branding: data.branding ? { ...DEFAULT_TENANT_BRANDING, ...data.branding } : DEFAULT_TENANT_BRANDING,
      sso_config: data.sso_config || null,
      settings: data.settings ? { ...DEFAULT_TENANT_SETTINGS, ...data.settings } : DEFAULT_TENANT_SETTINGS,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  }

  /**
   * Validate tenant configuration
   */
  static validateTenantConfig(tenant: Partial<Tenant>): string[] {
    const errors: string[] = [];

    if (!tenant.slug) {
      errors.push('Tenant slug is required');
    } else if (!isValidTenantSlug(tenant.slug)) {
      errors.push('Invalid tenant slug format');
    }

    if (!tenant.name || tenant.name.trim().length === 0) {
      errors.push('Tenant name is required');
    }

    if (tenant.branding?.primaryColor && !tenant.branding.primaryColor.match(/^#[0-9A-F]{6}$/i)) {
      errors.push('Invalid primary color format');
    }

    return errors;
  }
} 